'use client';

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore, type RolePreview } from '@/stores/auth-store';
import {
  getRoleHierarchyMatrix,
  listRoles,
} from '@/services/roles.service';
import {
  clearSessionRolePreview,
  setSessionRolePreview,
} from '@/services/auth.service';
import { HRM_PRODUCT_CODE } from '@/lib/api/config';
import type { OrgRoleHierarchyMatrixRow } from '@/types/role';

type PreviewOption = {
  key: string;
  roleId: string;
  roleName: string;
  levelId?: string | null;
  levelName?: string | null;
  roleCode?: string | null;
  permissions: string[];
  label: string;
};

function optionsFromMatrix(matrix: OrgRoleHierarchyMatrixRow[]): PreviewOption[] {
  const out: PreviewOption[] = [];
  for (const role of matrix) {
    const code = role.functionalRoleCode ?? role.name;
    if (role.levels?.length && role.usesCustomHierarchy) {
      const sorted = [...role.levels].sort((a, b) => a.rank - b.rank);
      for (const level of sorted) {
        // Never fall back to full role catalogue — that made every rank look like Admin.
        const perms =
          level.cumulativePermissionCodes?.length
            ? level.cumulativePermissionCodes
            : level.permissionCodes?.length
              ? level.permissionCodes
              : [];
        if (!perms.length) continue;
        out.push({
          key: `${role.orgRoleId}:${level.id}`,
          roleId: role.orgRoleId,
          roleName: role.name,
          levelId: level.id,
          levelName: level.name,
          roleCode: code,
          permissions: perms,
          label: `${role.name} · ${level.name}`,
        });
      }
    } else {
      out.push({
        key: role.orgRoleId,
        roleId: role.orgRoleId,
        roleName: role.name,
        levelId: null,
        levelName: null,
        roleCode: code,
        permissions: role.rolePermissions ?? [],
        label: role.name,
      });
    }
  }
  return out;
}

/**
 * Lists org HRM roles (and ranks when hierarchy exists) so you can preview
 * sidebar / page access AND API guards as that role (server session preview).
 */
export default function RolePreviewSwitcher({
  onPicked,
}: {
  onPicked?: () => void;
}) {
  const rolePreview = useAuthStore((s) => s.rolePreview);
  const setRolePreview = useAuthStore((s) => s.setRolePreview);
  const clearRolePreview = useAuthStore((s) => s.clearRolePreview);
  const syncPermissions = useAuthStore((s) => s.syncPermissions);
  const realUser = useAuthStore((s) => s.displayUser);
  const authUser = useAuthStore((s) => s.user);

  const canSwitch = useMemo(() => {
    // Once preview is active, keep the switcher usable (displayUser perms are overlaid).
    if (rolePreview) return true;
    if (!realUser) return false;
    if (realUser.isSuperAdmin) return true;
    const role = (realUser.authRole ?? '').toLowerCase();
    if (role === 'org_admin' || role === 'admin' || role.includes('super')) return true;
    const realPerms =
      authUser?.products?.find((p) => p.code.toUpperCase() === HRM_PRODUCT_CODE)?.permissions ??
      realUser.permissions ??
      [];
    const set = new Set(realPerms.map((p) => p.trim().toLowerCase()));
    return (
      set.has('identity.roles.read') ||
      set.has('identity.roles.update') ||
      set.has('platform.settings.update')
    );
  }, [realUser, authUser, rolePreview]);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matrixQuery = useQuery({
    queryKey: ['hrm', 'role-preview', 'matrix'],
    queryFn: () => getRoleHierarchyMatrix(HRM_PRODUCT_CODE),
    enabled: canSwitch && open,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const rolesQuery = useQuery({
    queryKey: ['hrm', 'role-preview', 'roles'],
    queryFn: () => listRoles(HRM_PRODUCT_CODE),
    enabled: canSwitch && open && (matrixQuery.isError || (matrixQuery.isSuccess && !(matrixQuery.data?.length))),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const options = useMemo((): PreviewOption[] => {
    if (matrixQuery.data?.length) return optionsFromMatrix(matrixQuery.data);
    return (rolesQuery.data ?? [])
      .filter((r) => r.isActive && !r.isDeleted)
      .map((r) => ({
        key: r.id,
        roleId: r.id,
        roleName: r.name,
        levelId: null,
        levelName: null,
        roleCode: r.functionalRoleCode ?? r.name,
        permissions: r.permissions ?? [],
        label: r.name,
      }));
  }, [matrixQuery.data, rolesQuery.data]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, q]);

  if (!canSwitch && !rolePreview) return null;

  const apply = async (opt: PreviewOption) => {
    setBusy(true);
    setError(null);
    const localPreview: RolePreview = {
      roleId: opt.roleId,
      roleName: opt.roleName,
      levelId: opt.levelId,
      levelName: opt.levelName,
      permissions: opt.permissions,
      roleCode: opt.roleCode,
    };
    try {
      const { rolePreview: serverPreview, products } = await setSessionRolePreview({
        orgRoleId: opt.roleId,
        orgRoleLevelId: opt.levelId ?? null,
      });
      syncPermissions(products);
      setRolePreview({
        roleId: serverPreview.orgRoleId,
        roleName: serverPreview.roleName,
        levelId: serverPreview.orgRoleLevelId,
        levelName: serverPreview.levelName,
        // Prefer server codes; fall back to matrix option if server returned empty.
        permissions:
          serverPreview.permissions?.length > 0
            ? serverPreview.permissions
            : opt.permissions,
        roleCode: serverPreview.roleCode,
      });
      setOpen(false);
      onPicked?.();
    } catch (err) {
      // Still switch the UI even if auth-service preview endpoint is unavailable.
      setRolePreview(localPreview);
      const existing =
        authUser?.products?.find((p) => p.code.toUpperCase() === HRM_PRODUCT_CODE) ??
        null;
      syncPermissions([
        {
          code: HRM_PRODUCT_CODE,
          plan: existing?.plan ?? 'default',
          permissions: opt.permissions,
          roleCode: opt.roleCode ?? existing?.roleCode,
          levelName: opt.levelName ?? existing?.levelName,
          levelRank: existing?.levelRank,
        },
      ]);
      setError(
        err instanceof Error
          ? `${err.message} — UI preview applied locally`
          : 'UI preview applied locally (API sync failed)',
      );
      setOpen(false);
      onPicked?.();
    } finally {
      setBusy(false);
    }
  };

  const exit = async () => {
    setBusy(true);
    setError(null);
    try {
      const products = await clearSessionRolePreview();
      syncPermissions(products);
    } catch {
      /* restore from last known non-preview is best-effort */
    } finally {
      clearRolePreview();
      setBusy(false);
      setOpen(false);
      onPicked?.();
    }
  };

  return (
    <div className="border-t border-slate-200 dark:border-slate-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="w-full flex items-center justify-between gap-2 px-4 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 transition-colors text-left disabled:opacity-60"
      >
        <span className="inline-flex items-center gap-2.5">
          <i className="ri-user-shared-line" />
          Switch role preview
        </span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-sm`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-[10px] leading-snug text-slate-400 px-1">
            Pick the same role and rank as a real user (e.g. Employee · Senior). UI and API use that exact permission set.
          </p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search roles…"
            disabled={busy}
            className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/40 disabled:opacity-60"
          />
          {(matrixQuery.isLoading || rolesQuery.isLoading) && (
            <p className="text-[11px] text-slate-400 px-1 py-2">Loading roles…</p>
          )}
          {(matrixQuery.isError && rolesQuery.isError) && (
            <p className="text-[11px] text-red-500 px-1 py-2">
              Could not load roles (need identity.roles.read).
            </p>
          )}
          {error && (
            <p className="text-[11px] text-red-500 px-1 py-1">{error}</p>
          )}
          <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800">
            {filtered.map((opt) => {
              const active =
                rolePreview?.roleId === opt.roleId &&
                (rolePreview?.levelId ?? null) === (opt.levelId ?? null);
              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={busy}
                  onClick={() => void apply(opt)}
                  className={`w-full text-left px-2.5 py-2 text-[11px] font-medium border-b border-slate-50 dark:border-slate-800/80 last:border-0 transition-colors disabled:opacity-60 ${
                    active
                      ? 'bg-[var(--hrm-primary-light)]/60 dark:bg-[var(--hrm-primary-dark)]/30 text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)]'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span className="block truncate">{opt.label}</span>
                  <span className="text-[10px] text-slate-400 font-normal">
                    {opt.permissions.length} permissions
                  </span>
                </button>
              );
            })}
            {!matrixQuery.isLoading && !rolesQuery.isLoading && filtered.length === 0 && (
              <p className="text-[11px] text-slate-400 px-2.5 py-3">No roles match.</p>
            )}
          </div>
          {rolePreview && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void exit()}
              className="w-full text-xs font-semibold py-1.5 rounded-lg text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)] hover:bg-[var(--hrm-primary-light)]/50 dark:hover:bg-[var(--hrm-primary-dark)]/20 disabled:opacity-60"
            >
              {busy ? 'Syncing…' : 'Exit preview (my real role)'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
